import React from 'react';
import { NavigateOptions, useNavigate } from 'react-router';
import { useTrackNavigation } from '~/hooks/analytics/useTrackNavigation';
import useRouteMatch from '~/hooks/useRouteMatch';
import { assertIsDefined } from '~/types/typeGuards';
import { Tab, TabBar, TabBarProps, TabProps } from '../TabBar/TabBar';

export interface NavigationTabProps extends Omit<TabProps, 'id'> {
  path: string;
}

export interface NavigationTabBarProps extends Omit<TabBarProps, 'children' | 'onSelect' | 'selected'> {
  children: React.ReactNode;
  onSelect?: (tab: NavigationTabProps) => boolean | void;
  navigateOptions?: NavigateOptions;
  ref?: React.Ref<HTMLDivElement>;
}

const NavigationTabComponent: React.FC<NavigationTabProps> = () => {
  throw new Error('NavigationTabBar.Tab must be used within a NavigationTabBar component');
};
NavigationTabComponent.displayName = 'NavigationTabBar.Tab';

type NavigationTabBarTabElement = React.ReactElement<NavigationTabProps, typeof NavigationTabComponent>;

const isNavigationTabBarTabElement = (child: React.ReactNode): child is NavigationTabBarTabElement => {
  return React.isValidElement<NavigationTabProps>(child) && child.type === NavigationTabComponent;
};

export const getNavigationTabs = (children: NavigationTabBarProps['children']): NavigationTabProps[] => {
  return React.Children.toArray(children).map((child) => {
    if (!isNavigationTabBarTabElement(child)) {
      throw new Error('NavigationTabBar children must be of type NavigationTabBar.Tab');
    }

    return child.props;
  });
};

const NavigationTabBar = ({
  tabListClassName,
  tabBarClassName,
  children,
  onSelect,
  variant = 'default',
  navigateOptions,
  ref,
}: NavigationTabBarProps) => {
  const navigate = useNavigate();
  const tabs = getNavigationTabs(children);
  const currentTab = useRouteMatch(tabs.map((tab) => tab.path)) || tabs[0]?.path;
  const trackNavigation = useTrackNavigation();

  return (
    <TabBar
      ref={ref}
      className={tabBarClassName}
      tabListClassName={tabListClassName}
      variant={variant}
      selected={currentTab}
      onSelect={(tab) => {
        if (tab.disabled) return;
        const navigationTab = tabs.find((t) => t.path === tab.id);
        assertIsDefined(navigationTab, 'Selected tab must exist in tabs array');
        if (onSelect?.(navigationTab) === false) return;
        trackNavigation('tabs', navigationTab.path);
        navigate(navigationTab.path, navigateOptions);
      }}
    >
      {tabs.map((tab) => (
        <Tab key={tab.path} {...tab} id={tab.path} />
      ))}
    </TabBar>
  );
};

export default Object.assign(NavigationTabBar, {
  Tab: NavigationTabComponent,
});
